/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag React.Fragment
 */

/**
 * @deprecated
 * This is the v1 admin consent modal, driven by the `usePermission` hook.
 *
 * The v2 equivalent is:
 *   `platform/packages/forge/forge-ui/src/bridge/methods/permissions/AdminConsentModal.tsx`
 *
 * The `styles` and `messages` exports from this file are
 * intentionally re-used by the v2 modal to keep the UI consistent while avoiding
 * duplication. Do not remove those exports.
 *
 * Do not add new UI logic here — add it to the v2 modal instead.
 */

import { jsx, type CompiledStyles } from '@compiled/react';
import { defineMessages, useIntl } from 'react-intl';

import Button from '@atlaskit/button/default/button';
import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading/heading';
import Link from '@atlaskit/link/link';
import ModalBody from '@atlaskit/modal-dialog/modal-body';
import ModalDialog from '@atlaskit/modal-dialog/modal-dialog';
import AKModalFooter from '@atlaskit/modal-dialog/modal-footer';
import ModalHeader from '@atlaskit/modal-dialog/modal-header';
import ModalTitle from '@atlaskit/modal-dialog/modal-title';
import ModalTransition from '@atlaskit/modal-dialog/modal-transition';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Flex } from '@atlaskit/primitives/compiled/flex';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';

import type { ModalState } from './usePermission';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles, @atlaskit/design-system/no-invalid-css-map -- Shared with v2 bridge modal for style consistency. See go/DSP-18766
export const styles: {
	readonly disclaimerContainer: CompiledStyles<{
		marginBottom: 'var(--ds-space-400)';
		marginTop: 'var(--ds-space-400)';
	}>;
	readonly modalFooterContainer: CompiledStyles<{
		borderTopColor: 'var(--ds-border)';
		borderTopStyle: 'solid';
		borderTopWidth: 'var(--ds-border-width)';
	}>;
	readonly rowContainer: CompiledStyles<{
		borderBottomColor: 'var(--ds-border)';
		borderBottomStyle: 'solid';
		borderBottomWidth: 'var(--ds-border-width)';
		paddingBottom: 'var(--ds-space-100)';
		paddingLeft: 'var(--ds-space-100)';
		paddingRight: 'var(--ds-space-100)';
		paddingTop: 'var(--ds-space-100)';
	}>;
	readonly titleContainer: CompiledStyles<{
		marginBottom: 'var(--ds-space-400)';
	}>;
} = cssMap({
	titleContainer: {
		marginBottom: token('space.400'),
	},
	modalFooterContainer: {
		borderTopStyle: 'solid',
		borderTopWidth: token('border.width'),
		borderTopColor: token('color.border'),
	},
	disclaimerContainer: {
		marginTop: token('space.400'),
		marginBottom: token('space.400'),
	},
	rowContainer: {
		paddingTop: token('space.100'),
		paddingBottom: token('space.100'),
		paddingRight: token('space.100'),
		paddingLeft: token('space.100'),
		borderBottomStyle: 'solid',
		borderBottomWidth: token('border.width'),
		borderBottomColor: token('color.border'),
	},
});

export const messages: {
	approveButtonLabel: {
		defaultMessage: string;
		description: string;
		id: string;
	};
	confirmErrorMessage: {
		defaultMessage: string;
		description: string;
		id: string;
	};
	declineButtonLabel: {
		defaultMessage: string;
		description: string;
		id: string;
	};
	egressDefault: {
		defaultMessage: string;
		description: string;
		id: string;
	};
	egressFetchBackendSide: {
		defaultMessage: string;
		description: string;
		id: string;
	};
	egressFetchClientSide: {
		defaultMessage: string;
		description: string;
		id: string;
	};
	egressFonts: {
		defaultMessage: string;
		description: string;
		id: string;
	};
	egressFrames: {
		defaultMessage: string;
		description: string;
		id: string;
	};
	egressImages: {
		defaultMessage: string;
		description: string;
		id: string;
	};
	egressMedia: {
		defaultMessage: string;
		description: string;
		id: string;
	};
	egressNavigation: {
		defaultMessage: string;
		description: string;
		id: string;
	};
	egressScripts: {
		defaultMessage: string;
		description: string;
		id: string;
	};
	egressStyles: {
		defaultMessage: string;
		description: string;
		id: string;
	};
	modalTransitionText: {
		defaultMessage: string;
		description: string;
		id: string;
	};
	modalTransitionTitle: {
		defaultMessage: string;
		description: string;
		id: string;
	};
	noticeAnd: {
		defaultMessage: string;
		description: string;
		id: string;
	};
	noticeForMoreDetails: {
		defaultMessage: string;
		description: string;
		id: string;
	};
	privacyPolicyLink: {
		defaultMessage: string;
		description: string;
		id: string;
	};
	remoteFallbackHeading: {
		defaultMessage: string;
		description: string;
		id: string;
	};
	remoteResetToDefault: {
		defaultMessage: string;
		description: string;
		id: string;
	};
	termsOfUseLink: {
		defaultMessage: string;
		description: string;
		id: string;
	};
} = defineMessages({
	declineButtonLabel: {
		id: 'forge-ui.adminConsentModal.decline.button.label',
		defaultMessage: 'Decline',
		description:
			'Text label on the decline button in the admin consent modal, which rejects the app permission request.',
	},
	approveButtonLabel: {
		id: 'forge-ui.adminConsentModal.approve.button.label',
		defaultMessage: 'Approve',
		description:
			'Text label on the approve button in the admin consent modal, which grants the app the requested permissions.',
	},
	noticeForMoreDetails: {
		id: 'forge-ui.adminConsentModal.notice.for-more-details',
		defaultMessage: "For more details, refer to {appName}'s",
		description:
			'First part of the notice text in the admin consent modal. The placeholder {appName} is substituted with the name of the Forge app requesting permissions.',
	},
	noticeAnd: {
		id: 'forge-ui.adminConsentModal.notice.and',
		defaultMessage: 'and',
		description:
			'Conjunction word displayed between the terms of use and privacy policy links in the admin consent modal notice.',
	},
	termsOfUseLink: {
		id: 'forge-ui.adminConsentModal.terms-of-use.link',
		defaultMessage: 'terms of use',
		description:
			'Clickable link text in the admin consent modal notice that navigates the user to the app terms of use page.',
	},
	privacyPolicyLink: {
		id: 'forge-ui.adminConsentModal.privacy-policy.link',
		defaultMessage: 'privacy policy',
		description:
			'Clickable link text in the admin consent modal notice that navigates the user to the app privacy policy page.',
	},
	modalTransitionTitle: {
		id: 'forge-ui.adminConsentModal.modal-transition.title',
		defaultMessage: 'Data storage, sharing, receiving and processing',
		description:
			'Heading displayed at the top of the admin consent modal, summarizing the data operations the app is requesting permission for.',
	},
	modalTransitionText: {
		id: 'forge-ui.adminConsentModal.modal-transition.text',
		defaultMessage:
			'{appName} is requesting to share with and receive data from the following domains:',
		description:
			'Body text in the admin consent modal explaining what the app wants to do. The placeholder {appName} is substituted with the name of the Forge app requesting data access.',
	},
	confirmErrorMessage: {
		id: 'forge-ui.adminConsentModal.confirm-error.message',
		defaultMessage: 'Something went wrong. Please try again or decline.',
		description:
			'Error message displayed in the admin consent modal when the API call to save permissions fails.',
	},
	egressFetchBackendSide: {
		id: 'forge-ui.adminConsentModal.egress.fetch-backend-side',
		defaultMessage: 'Fetch backend side',
		description:
			'Human-readable label for the FETCH_BACKEND_SIDE egress operation type displayed in the admin consent modal.',
	},
	egressFetchClientSide: {
		id: 'forge-ui.adminConsentModal.egress.fetch-client-side',
		defaultMessage: 'Fetch client side',
		description:
			'Human-readable label for the FETCH_CLIENT_SIDE egress operation type displayed in the admin consent modal.',
	},
	egressFonts: {
		id: 'forge-ui.adminConsentModal.egress.fonts',
		defaultMessage: 'Fonts',
		description:
			'Human-readable label for the FONTS egress operation type displayed in the admin consent modal.',
	},
	egressFrames: {
		id: 'forge-ui.adminConsentModal.egress.frames',
		defaultMessage: 'Frames',
		description:
			'Human-readable label for the FRAMES egress operation type displayed in the admin consent modal.',
	},
	egressImages: {
		id: 'forge-ui.adminConsentModal.egress.images',
		defaultMessage: 'Images',
		description:
			'Human-readable label for the IMAGES egress operation type displayed in the admin consent modal.',
	},
	egressMedia: {
		id: 'forge-ui.adminConsentModal.egress.media',
		defaultMessage: 'Media',
		description:
			'Human-readable label for the MEDIA egress operation type displayed in the admin consent modal.',
	},
	egressNavigation: {
		id: 'forge-ui.adminConsentModal.egress.navigation',
		defaultMessage: 'Navigation',
		description:
			'Human-readable label for the NAVIGATION egress operation type displayed in the admin consent modal.',
	},
	egressScripts: {
		id: 'forge-ui.adminConsentModal.egress.scripts',
		defaultMessage: 'Scripts',
		description:
			'Human-readable label for the SCRIPTS egress operation type displayed in the admin consent modal.',
	},
	egressStyles: {
		id: 'forge-ui.adminConsentModal.egress.styles',
		defaultMessage: 'Styles',
		description:
			'Human-readable label for the STYLES egress operation type displayed in the admin consent modal.',
	},
	egressDefault: {
		id: 'forge-ui.adminConsentModal.egress.default',
		defaultMessage: 'Egress',
		description:
			'Fallback human-readable label for an unknown egress operation type displayed in the admin consent modal.',
	},
	remoteFallbackHeading: {
		id: 'forge-ui.adminConsentModal.remote.fallback-heading',
		defaultMessage: 'Remote',
		description:
			'Fallback heading displayed in the admin consent modal when a remote key has no description in the app manifest.',
	},
	remoteResetToDefault: {
		id: 'forge-ui.adminConsentModal.remote.reset-to-default',
		defaultMessage: 'This remote is being reset to the default value',
		description:
			'Text displayed in the admin consent modal when a remote endpoint is being reset to its default value (null endpoint).',
	},
});

export const AdminModal = ({ modalState }: { modalState: ModalState }): JSX.Element => {
	const intl = useIntl();

	if (modalState.state === 'closed') {
		return <ModalTransition />;
	}

	const ModalFooter = () => {
		return (
			<Box xcss={styles.modalFooterContainer}>
				<AKModalFooter>
					<Inline space="space.075">
						<Button onClick={modalState.onCancel}>
							{intl.formatMessage(messages.declineButtonLabel)}
						</Button>
						<Button onClick={modalState.onConfirm} appearance="primary">
							{intl.formatMessage(messages.approveButtonLabel)}
						</Button>
					</Inline>
				</AKModalFooter>
			</Box>
		);
	};

	const renderDomains = () => {
		const getHumanReadableName = (operation: string) => {
			switch (operation) {
				case 'FETCH_BACKEND_SIDE':
					return 'Fetch backend side';
				case 'FETCH_CLIENT_SIDE':
					return 'Fetch client side';
				case 'FONTS':
					return 'Fonts';
				case 'FRAMES':
					return 'Frames';
				case 'IMAGES':
					return 'Images';
				case 'MEDIA':
					return 'Media';
				case 'NAVIGATION':
					return 'Navigation';
				case 'SCRIPTS':
					return 'Scripts';
				case 'STYLES':
					return 'Styles';
				default:
					return 'Egress';
			}
		};

		const domainInfo = modalState.domainInfo;

		if (Object.keys(domainInfo).length === 0) {
			return null;
		}

		if (domainInfo.type === 'REMOTE') {
			return (
				<Flex direction="column" gap="space.150">
					{domainInfo.payload.remotes.map((r) => {
						const manifestRemoteInfo = domainInfo.manifestRemoteMap[r.key];

						return (
							<Stack key={r.key} space="space.150">
								<Heading size="small">
									{manifestRemoteInfo ? manifestRemoteInfo.description : 'Remote'}
								</Heading>
								<Inline xcss={styles.rowContainer} spread="space-between" grow="fill">
									<Text>
										{r.configured.endpoint === null
											? 'This remote is being reset to the default value'
											: r.configured.endpoint}
									</Text>
									<Text>{manifestRemoteInfo ? manifestRemoteInfo.name : null}</Text>
								</Inline>
							</Stack>
						);
					})}
				</Flex>
			);
		}

		if (domainInfo.type === 'EGRESS') {
			return (
				<Flex direction="column" gap="space.150">
					{domainInfo.payload.egresses.map((e) => (
						<Stack key={e.key} space="space.150">
							<Heading size="xsmall">{e.description}</Heading>
							{e.configured.map((c) => {
								return c.type.map((t) => {
									return (
										<Inline xcss={styles.rowContainer} spread="space-between" grow="fill">
											<Text>{c.domain}</Text>
											<Text>{getHumanReadableName(t)}</Text>
										</Inline>
									);
								});
							})}
						</Stack>
					))}
				</Flex>
			);
		}
	};

	const Notice = () => {
		const { appName, termsOfUseLink, privacyPolicyLink } = modalState;
		if (termsOfUseLink && privacyPolicyLink) {
			return (
				<Text>
					{intl!.formatMessage(messages.noticeForMoreDetails, { appName: appName ?? 'the app' })}{' '}
					<Link href={termsOfUseLink}>{intl!.formatMessage(messages.termsOfUseLink)}</Link>{' '}
					{intl!.formatMessage(messages.noticeAnd)}{' '}
					<Link href={privacyPolicyLink}>{intl!.formatMessage(messages.privacyPolicyLink)}</Link>
				</Text>
			);
		} else if (termsOfUseLink) {
			return (
				<Text>
					{intl!.formatMessage(messages.noticeForMoreDetails, { appName: appName ?? 'the app' })}{' '}
					<Link href={termsOfUseLink}>{intl!.formatMessage(messages.termsOfUseLink)}</Link>
				</Text>
			);
		} else if (privacyPolicyLink) {
			return (
				<Text>
					{intl!.formatMessage(messages.noticeForMoreDetails, { appName: appName ?? 'the app' })}{' '}
					<Link href={privacyPolicyLink}>{intl!.formatMessage(messages.privacyPolicyLink)}</Link>
				</Text>
			);
		}
	};

	return (
		<ModalTransition>
			<ModalDialog
				onClose={() => {
					modalState.onCancel();
				}}
				width="large"
			>
				<ModalHeader>
					<Box xcss={styles.titleContainer}>
						<ModalTitle>{intl!.formatMessage(messages.modalTransitionTitle)}</ModalTitle>
					</Box>
				</ModalHeader>
				<ModalBody>
					<Stack space="space.300">
						<Text>
							{intl!.formatMessage(messages.modalTransitionText, {
								appName: modalState.appName ?? 'The app',
							})}
						</Text>
						<Box>
							{renderDomains()}
							<Box xcss={styles.disclaimerContainer}>
								<Notice />
							</Box>
						</Box>
					</Stack>
				</ModalBody>
				<ModalFooter />
			</ModalDialog>
		</ModalTransition>
	);
};
